import { useEffect, useRef } from 'react';

/**
 * Observe toutes les descendances portant une classe `.sh-reveal*`
 * et leur ajoute `.visible` lorsqu'elles entrent dans le viewport.
 *
 * Classes disponibles (à appliquer dans le JSX) :
 *   - sh-reveal         : fade + translate-up
 *   - sh-reveal-left    : slide-in gauche
 *   - sh-reveal-right   : slide-in droite
 *   - sh-reveal-scale   : scale-in
 *   - sh-stagger-1..6   : delays incrémentaux
 *
 * Usage :
 *   const ref = useRevealOnScroll();
 *   return <section ref={ref}> ... </section>
 *
 *   // ou sur tout le document :
 *   useRevealOnScroll({ rootSelector: 'body' });
 */
export default function useRevealOnScroll(options = {}) {
  const {
    threshold = 0.12,
    rootMargin = '0px 0px -40px 0px',
    selector = '.sh-reveal, .sh-reveal-left, .sh-reveal-right, .sh-reveal-scale',
  } = options;

  const ref = useRef(null);

  useEffect(() => {
    const root = ref.current || document;
    if (!root) return;

    const prefersReduced = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    const getTargets = () => root.querySelectorAll(selector);

    if (prefersReduced) {
      getTargets().forEach((el) => el.classList.add('visible'));
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      getTargets().forEach((el) => el.classList.add('visible'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('visible');
            io.unobserve(e.target);
          }
        });
      },
      { threshold, rootMargin }
    );

    getTargets().forEach((el) => io.observe(el));

    const mo = new MutationObserver(() => {
      getTargets().forEach((el) => {
        if (!el.classList.contains('visible')) io.observe(el);
      });
    });
    mo.observe(root, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, [threshold, rootMargin, selector]);

  return ref;
}
